import { useEffect, useMemo } from 'react'
import { match } from 'ts-pattern'

import type {
  Condition,
  ConditionalOperator
} from 'Automations/nodeDefinitions/conditions'
import {
  PHASE_DURATION_OPERATORS,
  PHASE_NAME_OPERATORS,
  isPhaseDurationOperator,
  isPhaseNameOperator,
  type PhaseDurationOperator,
  type PhaseDurationValue,
  type PhaseNameOperator
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings/phase'
import {
  PHASE_DURATION_OPERATOR_CONFLICTS,
  PHASE_DURATION_OPERATOR_LABELS,
  PHASE_NAME_OPERATOR_CONFLICTS,
  PHASE_NAME_OPERATOR_LABELS,
  PHASE_OPERATOR_KEYS,
  PHASE_OPERATOR_LABELS,
  type PhaseOperator
} from './phase.constants'
import {
  EMPTY_PHASE_DURATION_INPUT,
  phaseDurationFromInput
} from './phaseDurationInput'

export type PhaseSubOperator = PhaseNameOperator | PhaseDurationOperator

export type PhaseEditorValue = {
  operator?: PhaseSubOperator
  name?: string
  duration?: PhaseDurationValue
}

export const EMPTY_DURATION: PhaseDurationValue = phaseDurationFromInput(
  EMPTY_PHASE_DURATION_INPUT
)

export const readPhaseEditorValue = (value: unknown): PhaseEditorValue =>
  typeof value === 'object' && value !== null ? (value as PhaseEditorValue) : {}

export const appendPhaseTestId = (
  base: string | undefined,
  suffix: string
): string | undefined => (base ? `${base}.${suffix}` : undefined)

export const isPhasePropertyAvailable = ({
  siblingConditions
}: {
  siblingConditions: Array<Condition>
}): boolean => Boolean(getDefaultPhaseOperator({ siblingConditions }))

export const getDefaultPhaseOperator = ({
  siblingConditions
}: {
  siblingConditions: Array<Condition>
}): PhaseOperator | undefined => {
  const phaseSiblings = siblingConditions.filter(
    condition => condition.property === 'PHASE'
  )

  return PHASE_OPERATOR_KEYS.find(
    operator => availableSubOperatorsFor({ operator, phaseSiblings }).length > 0
  )
}

export const usePhaseOperatorState = (params: {
  operator: string
  value: unknown
  operators: Record<string, ConditionalOperator>
  siblingConditions: Array<Condition>
  onChange: (next: { operator?: string; value?: PhaseEditorValue }) => void
}) => {
  const { operator, value, operators, siblingConditions, onChange } = params
  const { operator: subOperator } = readPhaseEditorValue(value)

  const phaseSiblings = useMemo(
    () => siblingConditions.filter(condition => condition.property === 'PHASE'),
    [siblingConditions]
  )

  const availableSubOperators = availableSubOperatorsFor({
    operator,
    phaseSiblings
  })
  const subOperatorOptions = availableSubOperators.map(value => ({
    label: subOperatorLabel(value),
    value
  }))

  const operatorOptions = PHASE_OPERATOR_KEYS.filter(
    key =>
      key === operator ||
      availableSubOperatorsFor({ operator: key, phaseSiblings }).length > 0
  ).map(value => ({
    label: operators[value]?.label ?? PHASE_OPERATOR_LABELS[value],
    value
  }))

  const selectedSubOperator =
    availableSubOperators.find(candidate => candidate === subOperator) ??
    availableSubOperators.at(0)

  useEffect(() => {
    if (subOperator === selectedSubOperator) return
    onChange({ value: emptyValueFor(operator, selectedSubOperator) })
  }, [subOperator, selectedSubOperator, onChange, operator])

  const onPrimaryOperatorChange = (selected: string | number) => {
    const nextOperator = String(selected) as PhaseOperator
    const nextSubOperator = availableSubOperatorsFor({
      operator: nextOperator,
      phaseSiblings
    }).at(0)
    onChange({
      operator: nextOperator,
      value: emptyValueFor(nextOperator, nextSubOperator)
    })
  }

  return {
    operatorOptions,
    primaryOperatorDisabled: operatorOptions.length === 0,
    onPrimaryOperatorChange,
    subOperatorOptions,
    subOperatorDisabled: subOperatorOptions.length === 0
  }
}

const availableSubOperatorsFor = (params: {
  operator: string
  phaseSiblings: Array<Condition>
}): PhaseSubOperator[] => {
  const { operator, phaseSiblings } = params
  return match(operator)
    .with('NAME', () => {
      const used = phaseSiblings
        .filter(condition => condition.operator === 'NAME')
        .map(condition => readPhaseEditorValue(condition.value).operator)
        .filter(isPhaseNameOperator)
      return PHASE_NAME_OPERATORS.filter(candidate =>
        used.every(
          sibling => !PHASE_NAME_OPERATOR_CONFLICTS[candidate].includes(sibling)
        )
      )
    })
    .with('DURATION', () => {
      const used = phaseSiblings
        .filter(condition => condition.operator === 'DURATION')
        .map(condition => readPhaseEditorValue(condition.value).operator)
        .filter(isPhaseDurationOperator)
      return PHASE_DURATION_OPERATORS.filter(candidate =>
        used.every(
          sibling =>
            !PHASE_DURATION_OPERATOR_CONFLICTS[candidate].includes(sibling)
        )
      )
    })
    .otherwise(() => [])
}

const subOperatorLabel = (operator: PhaseSubOperator): string =>
  match(operator)
    .when(isPhaseNameOperator, value => PHASE_NAME_OPERATOR_LABELS[value])
    .when(
      isPhaseDurationOperator,
      value => PHASE_DURATION_OPERATOR_LABELS[value]
    )
    .exhaustive()

const emptyValueFor = (
  operator: string,
  subOperator?: PhaseSubOperator
): PhaseEditorValue =>
  match(operator)
    .with('NAME', () => ({
      operator: isPhaseNameOperator(subOperator) ? subOperator : undefined,
      name: ''
    }))
    .with('DURATION', () => ({
      operator: isPhaseDurationOperator(subOperator) ? subOperator : undefined,
      duration: EMPTY_DURATION
    }))
    .otherwise(() => ({}))
